'use client';

import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { HelpCircle, Loader2, Search, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/Dialog";
import { searchShipments } from '@/lib/API/Database/shipments/queries';
import { ShipmentResultsTable } from './_PageSections/ShipmentResultsTable';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

const TrackingPage = () => {
  const vulcanId = usePathname().split('/')[2];
  const [customerBranding, setCustomerBranding] = useState(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showResultsTable, setShowResultsTable] = useState(false);
  const pageSize = 10;
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / pageSize);

  useEffect(() => {
    const fetchCustomerBranding = async () => {
      try {
        const response = await fetch(`/api/customer-branding?domain=${window.location.hostname}`);
        const data = await response.json();
        setCustomerBranding(data);
        if (data.primaryColor) {
          document.documentElement.style.setProperty('--customer-primary', data.primaryColor);
          document.documentElement.style.setProperty('--customer-secondary', data.secondaryColor);
        }
      } catch (error) {
        console.error('Error fetching customer branding:', error);
      }
    };
    fetchCustomerBranding();
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    setShowResultsTable(false);
    e.preventDefault();
    setSearching(true);
    try {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) return;

      const results = await searchShipments(encodeURIComponent(trimmedQuery), "", currentPage, pageSize, true);
      setSearchResults(results.results);
      setTotal(results.total);
      setShowResultsTable(true);
    } catch (error) {
      alert('Could not fetch shipment data. Please try again later.');
      console.error('Error fetching shipment data:', error);
    } finally {
      setSearching(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Create synthetic event
    const syntheticEvent = {
      preventDefault: () => { },
    } as React.FormEvent<Element>;

    handleSearch(syntheticEvent);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch(e);
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults([]);
    setShowResultsTable(false);
  };

  useEffect(() => {
    if(!query) {
      setSearchResults([]);
      setShowResultsTable(false);
    }
  }, [query]);

  return (
    <div className="w-full max-w-none px-4 py-16 md:py-24">
      <div className="grid grid-cols-1 gap-8">
        <div className="text-center">
          {customerBranding?.logo && (
            <img
              src={customerBranding.logo}
              alt={`${customerBranding.name} logo`}
              className="h-16 mx-auto mb-4"
            />
          )}
          <h1 className="text-3xl font-bold mb-2">
            {customerBranding?.name || 'SupplySense 360'} Tracking
          </h1>
          <Dialog>
            <DialogTrigger>
              <div className="flex items-center gap-2">
                <p className="text-gray-800">Enter your tracking number</p>
                <HelpCircle className="h-4 w-4 text-gray-600" />
              </div>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Search using:</DialogTitle>
              </DialogHeader>
              <ul className="space-y-2">
                <li>House Air Waybill (HAWB)</li>
                <li>Booking Number</li>
                <li>Container Number</li>
                <li>Shipment Number</li>
                <li>Customer Reference</li>
                <li>Package Number</li>
                <li>Bill of Lading (B/L)</li>
              </ul>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 gap-2 max-w-5xl w-2/3 items-center mx-auto">
          <div className="relative flex justify-center">
            <Input
              type="search"
              placeholder="Enter a tracking number..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full p-2 rounded-lg pr-16"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
              {query && (
                <>
                  <button onClick={clearSearch}
                    className="hover:text-gray-300 transition-colors">
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                  <span className="text-gray-500">|</span>
                </>
              )}
              {query && (
                <button
                  onClick={handleSearch}
                  className="hover:text-gray-300 transition-colors"
                  disabled={!query || searching}
                >
                  <Search className="w-4 h-4 text-blue-500" />
                </button>
              )}
            </div>
          </div>
          <span className="text-center italic text-md text-muted-forground">We can only search for shipments within the last 3 months. <Link href="/auth/login"
            className="text-indigo-600 hover:text-indigo-500">Login</Link> to search for historical shipments.</span>
            <span className="text-center italic text-md">Examples: SDTE-1410326, AGYE-2210320, KOQS-9372940, JUDT-1382592</span>

          <div className="flex justify-center mt-4">
            <Button onClick={handleSearch}
              disabled={!query || searching}
              className="bg-blue-500 hover:bg-blue-600 text-white w-1/2">
              {searching ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Searching</>
              ) : (
                <><Search className="mr-2 h-4 w-4" /> Track</>
              )}
            </Button>
          </div>
        </div>

        {!(showResultsTable) ? null : (
          <Card className="w-full max-w-none mx-0 p-6">
            <ShipmentResultsTable
              results={searchResults}
              vulcanId={vulcanId}
              searchQuery={query}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrackingPage;
